import React, { useEffect, useState } from 'react';
import { 
    getInsightDashboard, 
    getPerformanceAnalytics, 
    getRealtimeAnalytics 
} from '../../../../services/api.service';
import { 
    InsightDashboardModel, 
    PerformanceAnalyticsModel, 
    RealtimeAnalyticsModel,
    InsightDashboardSummary 
} from '../../../../model/insights.model';
import { 
    Box, 
    Card, 
    CardContent, 
    Typography, 
    Grid, 
    CircularProgress, 
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    Alert
} from '@mui/material';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faUsers, 
    faChartBar, 
    faArrowTrendUp, 
    faClock, 
    faCheckCircle, 
    faHourglassHalf, 
    faChartLine 
} from "@fortawesome/free-solid-svg-icons";
import './insight-dashboard.component.scss';

interface InsightDashboardComponentProps {
    companyId: string | undefined;
    jobQuestionId: string | undefined;
}

const InsightDashboardComponent: React.FC<InsightDashboardComponentProps> = ({ 
    companyId, 
    jobQuestionId 
}) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dashboardData, setDashboardData] = useState<InsightDashboardModel | null>(null);
    const [performanceData, setPerformanceData] = useState<PerformanceAnalyticsModel | null>(null);
    const [realtimeData, setRealtimeData] = useState<RealtimeAnalyticsModel | null>(null);
    const [summary, setSummary] = useState<InsightDashboardSummary | null>(null);

    useEffect(() => {
        if (companyId && jobQuestionId) {
            fetchDashboardData();
        }
    }, [companyId, jobQuestionId]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch all dashboard data in parallel
            const [dashboardRes, performanceRes, realtimeRes] = await Promise.all([
                getInsightDashboard(companyId, jobQuestionId),
                getPerformanceAnalytics(companyId, jobQuestionId),
                getRealtimeAnalytics(companyId, jobQuestionId)
            ]);

            if (dashboardRes.data.is_success) {
                setDashboardData(dashboardRes.data.data);
                setSummary(dashboardRes.data.summary);
            }

            if (performanceRes.data.is_success) {
                setPerformanceData(performanceRes.data.data);
            }

            if (realtimeRes.data.is_success) {
                setRealtimeData(realtimeRes.data.data);
            }

        } catch (err: any) {
            setError(err.message || 'Failed to fetch dashboard data');
            console.error('Error fetching dashboard data:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num: number) => {
        return num.toLocaleString();
    };

    const formatPercentage = (num: number) => {
        return `${num}%`;
    };

    const formatDuration = (seconds: number) => {
        if (!seconds) return '0:00';
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const getScoreColor = (score: number) => {
        if (score >= 90) return '#4caf50';
        if (score >= 70) return '#8bc34a';
        if (score >= 50) return '#ff9800';
        return '#f44336';
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {error}
            </Alert>
        );
    }

    return (
        <Box className="insight-dashboard">
            <Typography variant="h4" gutterBottom className="dashboard-title">
                แดชบอร์ดเชิงลึก (Insight Dashboard)
            </Typography>

            {/* Overview Cards */}
            {dashboardData && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card className="overview-card">
                            <CardContent>
                                <Box display="flex" alignItems="center">
                                    <FontAwesomeIcon icon={faUsers} className="card-icon" />
                                    <Box ml={2}>
                                        <Typography variant="h4" className="card-number">
                                            {formatNumber(dashboardData.overview.total_candidates)}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            ผู้สมัครทั้งหมด
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card className="overview-card">
                            <CardContent>
                                <Box display="flex" alignItems="center">
                                    <FontAwesomeIcon icon={faCheckCircle} className="card-icon" />
                                    <Box ml={2}>
                                        <Typography variant="h4" className="card-number">
                                            {formatNumber(dashboardData.overview.completed_interviews)}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            สัมภาษณ์เสร็จสิ้น
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card className="overview-card">
                            <CardContent>
                                <Box display="flex" alignItems="center">
                                    <FontAwesomeIcon icon={faHourglassHalf} className="card-icon" />
                                    <Box ml={2}>
                                        <Typography variant="h4" className="card-number">
                                            {formatNumber(dashboardData.overview.not_completed_interviews)}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            สัมภาษณ์ไม่เสร็จสิ้น
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card className="overview-card">
                            <CardContent>
                                <Box display="flex" alignItems="center">
                                    <FontAwesomeIcon icon={faArrowTrendUp} className="card-icon" />
                                    <Box ml={2}>
                                        <Typography variant="h4" className="card-number">
                                            {formatPercentage(dashboardData.overview.average_completion_rate)}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            อัตราการเสร็จสิ้น
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card className="overview-card">
                            <CardContent>
                                <Box display="flex" alignItems="center">
                                    <FontAwesomeIcon icon={faClock} className="card-icon" />
                                    <Box ml={2}>
                                        <Typography variant="h4" className="card-number">
                                            {formatDuration(dashboardData.overview.average_duration)}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            ระยะเวลาเฉลี่ย
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Performance Metrics */}
            {dashboardData && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    คะแนนเฉลี่ยตามหมวดหมู่
                                </Typography>
                                <Box mt={2}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="body2">Job Fit Score</Typography>
                                        <Typography variant="h6" style={{ color: getScoreColor(dashboardData.performance_metrics.average_scores.job_fit) }}>
                                            {dashboardData.performance_metrics.average_scores.job_fit}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="body2">Skill Fit Score</Typography>
                                        <Typography variant="h6" style={{ color: getScoreColor(dashboardData.performance_metrics.average_scores.skill_fit) }}>
                                            {dashboardData.performance_metrics.average_scores.skill_fit}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="body2">Experience Fit Score</Typography>
                                        <Typography variant="h6" style={{ color: getScoreColor(dashboardData.performance_metrics.average_scores.experience_fit) }}>
                                            {dashboardData.performance_metrics.average_scores.experience_fit}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2">Multiple Choice Score</Typography>
                                        <Typography variant="h6" style={{ color: getScoreColor(dashboardData.performance_metrics.average_scores.multiple_choice) }}>
                                            {dashboardData.performance_metrics.average_scores.multiple_choice}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    การกระจายของคะแนน
                                </Typography>
                                <Box mt={2}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Chip label="ยอดเยี่ยม (90-100)" color="success" size="small" />
                                        <Typography variant="h6">
                                            {dashboardData.performance_metrics.score_distribution.excellent}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Chip label="ดี (70-89)" color="primary" size="small" />
                                        <Typography variant="h6">
                                            {dashboardData.performance_metrics.score_distribution.good}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Chip label="ปานกลาง (50-69)" color="warning" size="small" />
                                        <Typography variant="h6">
                                            {dashboardData.performance_metrics.score_distribution.average}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Chip label="ต่ำกว่าเกณฑ์ (<50)" color="error" size="small" />
                                        <Typography variant="h6">
                                            {dashboardData.performance_metrics.score_distribution.below_average}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Source Analytics */}
            {dashboardData && dashboardData.source_analytics.top_sources.length > 0 && (
                <Card sx={{ mb: 4 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            แหล่งที่มาของผู้สมัคร
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>แหล่งที่มา</TableCell>
                                        <TableCell align="right">จำนวน</TableCell>
                                        <TableCell align="right">เปอร์เซ็นต์</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dashboardData.source_analytics.top_sources.map((source, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{source.source}</TableCell>
                                            <TableCell align="right">{source.count}</TableCell>
                                            <TableCell align="right">
                                                {Math.round((source.count / dashboardData.overview.total_candidates) * 100)}%
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {/* Real-time Activity */}
            {realtimeData && (
                <Card sx={{ mb: 4 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            กิจกรรมล่าสุด (24 ชั่วโมง)
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={6} md={3}>
                                <Box textAlign="center">
                                    <Typography variant="h4" color="primary">
                                        {realtimeData.last_24_hours.new_candidates}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        ผู้สมัครใหม่
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Box textAlign="center">
                                    <Typography variant="h4" color="success.main">
                                        {realtimeData.last_24_hours.completed_interviews}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        สัมภาษณ์เสร็จสิ้น
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Box textAlign="center">
                                    <Typography variant="h4" color="warning.main">
                                        {realtimeData.current_status.interviews_in_progress}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        กำลังสัมภาษณ์
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Box textAlign="center">
                                    <Typography variant="h4" color="info.main">
                                        {realtimeData.current_status.pending_reviews}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        รอตรวจสอบ
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {realtimeData.recent_activity.length > 0 && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="subtitle1" gutterBottom>
                                    กิจกรรมล่าสุด
                                </Typography>
                                <Box maxHeight="300px" overflow="auto">
                                    {realtimeData.recent_activity.slice(0, 10).map((activity, index) => (
                                        <Box key={index} display="flex" justifyContent="space-between" alignItems="center" py={1}>
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {activity.candidate_name}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {activity.action}
                                                </Typography>
                                            </Box>
                                            <Chip 
                                                label={activity.action === 'Started Interview' ? 'เริ่มต้น' : 'เสร็จสิ้น'}
                                                color={activity.action === 'Started Interview' ? 'warning' : 'success'}
                                                size="small"
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Performance Analytics */}
            {performanceData && performanceData.improvement_areas.length > 0 && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            พื้นที่ที่ต้องปรับปรุง
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ทักษะ</TableCell>
                                        <TableCell align="right">คะแนนเฉลี่ย</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {performanceData.improvement_areas.map((area, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{area.skill}</TableCell>
                                            <TableCell align="right">
                                                <Typography 
                                                    variant="body2" 
                                                    style={{ color: getScoreColor(area.average) }}
                                                >
                                                    {area.average}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default InsightDashboardComponent; 